import type { CorrespondenceDraft } from "@platform/api";

/** What one row of the drafts list shows. */
export interface DraftSummary {
  id: string;
  threadId: string;
  /**
   * The conversation this answers, prefixed the way a reply is.
   *
   * A reply carries no subject of its own, so the row shows the thread's with
   * `Re:` in front — which is also what the carrier will see when it goes.
   */
  subject: string;
  /** The opening of what was written, for the row's two lines. */
  preview: string;
  /** `Drafted Sep 27`, in the viewer's locale. */
  draftedAt: string;
  /** Who it is addressed to, by display name where the directory gave one. */
  to: string;
  /** Splits the list: RUMI's await approval, the analyst's are theirs. */
  byRumi: boolean;
}

/**
 * Who the reply goes to, as the row names them.
 *
 * `To` recipients only. A copied address is on the message and is not who it
 * is addressed to, and a row listing both would read as though the reply were
 * going somewhere it is not.
 */
export const recipientsOf = (draft: CorrespondenceDraft): string => {
  const to = (draft.recipients ?? []).filter(
    (r) => r.role === "RECIPIENT_ROLE_TO",
  );

  return to
    .map((r) => r.displayName || r.address || "")
    .filter(Boolean)
    .join(", ");
};

/**
 * `Drafted Sep 27`, or empty where the timestamp will not parse.
 *
 * No year, for the reason the thread rows carry none: a drafts list is read
 * for what is waiting now, and an unsent reply from last year is a different
 * problem from the one this surface is for.
 */
export const formatDraftedAt = (
  timestamp: string | undefined,
  locale?: string,
): string => {
  if (!timestamp) return "";

  const at = new Date(timestamp);

  if (Number.isNaN(at.getTime())) return "";

  return at.toLocaleDateString(locale, { month: "short", day: "numeric" });
};

/**
 * The opening of the draft, with its line breaks flattened.
 *
 * A preview sits on two lines, and a body written as paragraphs would
 * otherwise show its first line and a blank one.
 */
export const previewOf = (body: string | undefined): string =>
  (body ?? "").replace(/\s+/gu, " ").trim();

export const toDraftSummary = (
  draft: CorrespondenceDraft,
  locale?: string,
): DraftSummary => {
  const subject = draft.subject ?? "";

  return {
    id: draft.messageId ?? "",
    threadId: draft.threadId ?? "",
    // Only one `Re:`. A thread opened by a reply already carries it, and
    // stacking them is what mail clients are mocked for.
    subject: /^re:/iu.test(subject) ? subject : `Re: ${subject}`,
    preview: previewOf(draft.body),
    draftedAt: formatDraftedAt(draft.draftedAt, locale),
    to: recipientsOf(draft),
    byRumi: draft.author === "MESSAGE_AUTHOR_RUMI",
  };
};
